/**
 * Geometry Editing Types
 *
 * Type definitions for the geometry editing system (Epic 2.3).
 * Includes types for zone creation, surface editing, fenestration placement,
 * adjacency detection, and construction assembly building.
 */

import type { Vector3, BoundaryCondition, FenestrationType } from './types';

// ============================================================================
// Edit Mode Types
// ============================================================================

/**
 * Available editing modes in the geometry editor.
 */
export type EditMode =
  | 'none'
  | 'select'
  | 'zone-create'
  | 'footprint-draw'
  | 'surface-edit'
  | 'surface-move'
  | 'surface-resize'
  | 'fenestration-place'
  | 'adjacency-detect';

/**
 * Tool types available in the 2D floor plan editor.
 */
export type DrawingTool = 'select' | 'polygon' | 'rectangle' | 'extrude';

// ============================================================================
// Zone Creation Types
// ============================================================================

/**
 * Method for creating a new zone.
 */
export type ZoneCreationMethod = 'simple-box' | 'extrude-footprint' | 'detailed-vertices';

/**
 * Construction assignments for zone surfaces.
 */
export interface ZoneConstructions {
  /** Construction for exterior walls */
  exteriorWalls: string;
  /** Construction for interior walls */
  interiorWalls: string;
  /** Construction for floor surfaces */
  floor: string;
  /** Construction for ceiling/roof surfaces */
  ceiling: string;
}

/**
 * Configuration for creating a simple box zone.
 */
export interface SimpleBoxConfig {
  /** Zone name */
  name: string;
  /** Origin point (X, Y, Z) in meters */
  origin: Vector3;
  /** Zone dimensions in meters */
  dimensions: {
    length: number; // X dimension
    width: number; // Y dimension
    height: number; // Z dimension
  };
  /** Construction assignments */
  constructions: ZoneConstructions;
  /** Direction of relative north (degrees) */
  directionOfRelativeNorth?: number;
}

/**
 * Configuration for creating a zone by extruding a footprint.
 */
export interface FootprintExtrudeConfig {
  /** Zone name */
  name: string;
  /** 2D footprint vertices (in X-Y plane) */
  footprint: Array<{ x: number; y: number }>;
  /** Base Z coordinate (floor level) */
  baseZ: number;
  /** Zone height in meters */
  height: number;
  /** Construction assignments */
  constructions: ZoneConstructions;
  /** Direction of relative north (degrees) */
  directionOfRelativeNorth?: number;
}

/**
 * Complete zone creation configuration.
 */
export interface ZoneCreateConfig {
  method: ZoneCreationMethod;
  simpleBox?: SimpleBoxConfig;
  footprintExtrude?: FootprintExtrudeConfig;
}

// ============================================================================
// Floor Plan Drawing Types
// ============================================================================

/**
 * A point in 2D space (for floor plan drawing).
 */
export interface Point2D {
  x: number;
  y: number;
}

/**
 * Snap mode options for 2D drawing.
 */
export interface SnapSettings {
  /** Enable snap to grid */
  grid: boolean;
  /** Grid size in meters */
  gridSize: number;
  /** Enable snap to vertices */
  vertex: boolean;
  /** Vertex snap distance in meters */
  vertexDistance: number;
  /** Enable snap to edges */
  edge: boolean;
  /** Edge snap distance in meters */
  edgeDistance: number;
}

/**
 * State for a polygon being drawn.
 */
export interface DrawingState {
  /** Whether actively drawing */
  isDrawing: boolean;
  /** Vertices drawn so far */
  vertices: Point2D[];
  /** Current mouse position (for preview) */
  currentPosition: Point2D | null;
  /** Whether the polygon is closed */
  isClosed: boolean;
}

/**
 * Floor level for plan view.
 */
export interface FloorLevel {
  /** Floor name */
  name: string;
  /** Z coordinate of floor */
  z: number;
  /** Default height for zones on this floor */
  defaultHeight: number;
}

// ============================================================================
// Surface Editing Types
// ============================================================================

/**
 * Types of surface editing operations.
 */
export type SurfaceEditOperationType =
  | 'move'
  | 'resize'
  | 'split'
  | 'merge'
  | 'construction'
  | 'flip'
  | 'delete'
  | 'boundary';

/**
 * A surface editing operation (for history tracking).
 */
export interface SurfaceEditOperation {
  type: SurfaceEditOperationType;
  surfaceIds: string[];
  parameters: Record<string, unknown>;
}

/**
 * Configuration for moving a surface.
 */
export interface SurfaceMoveConfig {
  /** Surface ID to move */
  surfaceId: string;
  /** Translation vector */
  translation: Vector3;
}

/**
 * Configuration for resizing a surface.
 */
export interface SurfaceResizeConfig {
  /** Surface ID to resize */
  surfaceId: string;
  /** New vertices */
  vertices: Vector3[];
}

/**
 * Configuration for splitting a surface.
 */
export interface SurfaceSplitConfig {
  /** Surface ID to split */
  surfaceId: string;
  /** Split line start point */
  splitStart: Vector3;
  /** Split line end point */
  splitEnd: Vector3;
}

/**
 * Configuration for merging surfaces.
 */
export interface SurfaceMergeConfig {
  /** Surface IDs to merge */
  surfaceIds: string[];
  /** Name for the merged surface */
  newName: string;
}

/**
 * Configuration for changing surface construction.
 */
export interface SurfaceConstructionConfig {
  /** Surface IDs to update */
  surfaceIds: string[];
  /** New construction name */
  construction: string;
}

/**
 * Configuration for changing surface boundary condition.
 */
export interface SurfaceBoundaryConfig {
  /** Surface ID to update */
  surfaceId: string;
  /** New boundary condition */
  boundaryCondition: BoundaryCondition;
  /** Outside boundary condition object (for 'surface' type) */
  outsideBoundaryObject?: string;
}

// ============================================================================
// Fenestration Placement Types
// ============================================================================

/**
 * Configuration for placing a fenestration on a surface.
 */
export interface FenestrationPlaceConfig {
  /** Parent surface ID */
  surfaceId: string;
  /** Fenestration type */
  type: FenestrationType;
  /** Fenestration name */
  name: string;
  /** Construction name */
  construction: string;
  /** Width in meters */
  width: number;
  /** Height in meters */
  height: number;
  /** Sill height (distance from floor) in meters */
  sillHeight: number;
  /** Horizontal position on surface (0-1, left to right) */
  horizontalPosition: number;
  /** Frame and divider name (optional) */
  frameAndDivider?: string;
  /** Multiplier */
  multiplier?: number;
}

/**
 * Window type presets.
 */
export interface WindowPreset {
  id: string;
  name: string;
  description: string;
  width: number;
  height: number;
  sillHeight: number;
  construction: string;
}

/**
 * Door type presets.
 */
export interface DoorPreset {
  id: string;
  name: string;
  description: string;
  width: number;
  height: number;
  construction: string;
}

// ============================================================================
// Adjacency Detection Types
// ============================================================================

/**
 * Quality of the match between two adjacent surfaces.
 */
export type MatchQuality = 'exact' | 'close' | 'partial';

/**
 * Suggested action for adjacent surfaces.
 */
export type AdjacencySuggestion = 'link' | 'merge' | 'review';

/**
 * Result of adjacency detection between two surfaces.
 */
export interface AdjacencyResult {
  /** First surface ID */
  surface1Id: string;
  /** First surface name */
  surface1Name: string;
  /** First surface zone */
  surface1Zone: string;
  /** Second surface ID */
  surface2Id: string;
  /** Second surface name */
  surface2Name: string;
  /** Second surface zone */
  surface2Zone: string;
  /** Quality of the geometric match */
  matchQuality: MatchQuality;
  /** Overlap area in square meters */
  overlapArea: number;
  /** Normal alignment score (1 = perfectly opposite) */
  normalAlignment: number;
  /** Distance between surface centers */
  centerDistance: number;
  /** Suggested action */
  suggestedAction: AdjacencySuggestion;
  /** Whether the adjacency has been linked */
  isLinked: boolean;
}

/**
 * Settings for adjacency detection algorithm.
 */
export interface AdjacencyDetectionSettings {
  /** Maximum distance between surfaces to consider adjacent (meters) */
  maxDistance: number;
  /** Minimum overlap area to consider adjacent (square meters) */
  minOverlapArea: number;
  /** Minimum normal alignment to consider adjacent (0-1) */
  minNormalAlignment: number;
  /** Tolerance for vertex matching (meters) */
  vertexTolerance: number;
}

// ============================================================================
// Construction Assembly Types
// ============================================================================

/**
 * A material layer in a construction assembly.
 */
export interface MaterialLayer {
  /** Unique layer ID */
  id: string;
  /** Material name (references Material object) */
  materialName: string;
  /** Layer thickness in meters */
  thickness: number;
  /** Material category for display */
  category: 'masonry' | 'insulation' | 'air-gap' | 'finish' | 'other';
}

/**
 * Material properties (from IDF Material objects).
 */
export interface MaterialProperties {
  /** Material name */
  name: string;
  /** Roughness */
  roughness: 'VeryRough' | 'Rough' | 'MediumRough' | 'MediumSmooth' | 'Smooth' | 'VerySmooth';
  /** Thickness in meters */
  thickness: number;
  /** Conductivity (W/m·K) */
  conductivity: number;
  /** Density (kg/m³) */
  density: number;
  /** Specific heat (J/kg·K) */
  specificHeat: number;
  /** Thermal absorptance */
  thermalAbsorptance?: number;
  /** Solar absorptance */
  solarAbsorptance?: number;
  /** Visible absorptance */
  visibleAbsorptance?: number;
}

/**
 * Air gap layer properties.
 */
export interface AirGapProperties {
  /** Air gap name */
  name: string;
  /** Thermal resistance (m²·K/W) */
  thermalResistance: number;
}

/**
 * No-mass material properties.
 */
export interface NoMassProperties {
  /** Material name */
  name: string;
  /** Roughness */
  roughness: string;
  /** Thermal resistance (m²·K/W) */
  thermalResistance: number;
  /** Thermal absorptance */
  thermalAbsorptance?: number;
  /** Solar absorptance */
  solarAbsorptance?: number;
  /** Visible absorptance */
  visibleAbsorptance?: number;
}

/**
 * Construction assembly definition.
 */
export interface ConstructionAssembly {
  /** Construction name */
  name: string;
  /** Construction type */
  type: 'opaque' | 'window' | 'internal-source';
  /** Layers (outside to inside) */
  layers: MaterialLayer[];
  /** Total thickness in meters */
  totalThickness: number;
  /** Calculated U-value (W/m²·K) */
  uValue: number;
  /** Calculated R-value (m²·K/W) */
  rValue: number;
  /** Source IDF object ID */
  sourceId?: string;
}

/**
 * Configuration for creating a new construction.
 */
export interface ConstructionCreateConfig {
  name: string;
  layers: Array<{
    materialName: string;
    isAirGap: boolean;
  }>;
}

// ============================================================================
// Undo/Redo Types
// ============================================================================

/**
 * Types of geometry actions for undo/redo.
 */
export type GeometryActionType =
  | 'zone-create'
  | 'zone-delete'
  | 'zone-update'
  | 'surface-create'
  | 'surface-delete'
  | 'surface-update'
  | 'surface-move'
  | 'surface-resize'
  | 'surface-split'
  | 'surface-merge'
  | 'surface-flip'
  | 'fenestration-create'
  | 'fenestration-delete'
  | 'fenestration-update'
  | 'adjacency-link'
  | 'adjacency-unlink'
  | 'construction-create'
  | 'construction-update'
  | 'construction-delete';

/**
 * A geometry action for undo/redo tracking.
 */
export interface GeometryAction {
  /** Action type */
  type: GeometryActionType;
  /** Timestamp of action */
  timestamp: number;
  /** Human-readable description */
  description: string;
  /** Data to apply the action */
  data: Record<string, unknown>;
  /** Data to reverse the action */
  inverseData: Record<string, unknown>;
  /** IDF object IDs created by this action */
  createdIds: string[];
  /** IDF object IDs deleted by this action */
  deletedIds: string[];
}

// ============================================================================
// Editor State Types
// ============================================================================

/**
 * Selection state in the editor.
 */
export interface EditorSelection {
  /** Selected zone IDs */
  zoneIds: string[];
  /** Selected surface IDs */
  surfaceIds: string[];
  /** Selected fenestration IDs */
  fenestrationIds: string[];
}

/**
 * Notification for user feedback.
 */
export interface EditorNotification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  timestamp: number;
}

/**
 * State for the Zone Creation Wizard.
 */
export interface ZoneWizardState {
  /** Whether the wizard is open */
  isOpen: boolean;
  /** Current wizard step */
  step: number;
  /** Selected creation method */
  method: ZoneCreationMethod;
  /** Configuration being built */
  config: Partial<ZoneCreateConfig>;
}

/**
 * State for the Fenestration Placer.
 */
export interface FenestrationPlacerState {
  /** Whether actively placing */
  isPlacing: boolean;
  /** Selected surface for placement */
  targetSurfaceId: string | null;
  /** Current placement configuration */
  config: Partial<FenestrationPlaceConfig>;
  /** Preview position on surface */
  previewPosition: Vector3 | null;
}

/**
 * State for the Adjacency Detector.
 */
export interface AdjacencyDetectorState {
  /** Whether detection has been run */
  hasRun: boolean;
  /** Detection results */
  results: AdjacencyResult[];
  /** Currently reviewing result index */
  reviewingIndex: number | null;
  /** Detection settings */
  settings: AdjacencyDetectionSettings;
}

/**
 * State for the Construction Builder.
 */
export interface ConstructionBuilderState {
  /** Whether the builder is open */
  isOpen: boolean;
  /** Construction being edited */
  editingConstruction: ConstructionAssembly | null;
  /** Whether creating new vs editing existing */
  isNew: boolean;
}

// ============================================================================
// Export Helpers
// ============================================================================

/**
 * Default snap settings.
 */
export const DEFAULT_SNAP_SETTINGS: SnapSettings = {
  grid: true,
  gridSize: 0.5,
  vertex: true,
  vertexDistance: 0.25,
  edge: true,
  edgeDistance: 0.25,
};

/**
 * Default adjacency detection settings.
 */
export const DEFAULT_ADJACENCY_SETTINGS: AdjacencyDetectionSettings = {
  maxDistance: 0.1,
  minOverlapArea: 0.5,
  minNormalAlignment: 0.95,
  vertexTolerance: 0.01,
};

/**
 * Default zone constructions.
 */
export const DEFAULT_ZONE_CONSTRUCTIONS: ZoneConstructions = {
  exteriorWalls: '',
  interiorWalls: '',
  floor: '',
  ceiling: '',
};

/**
 * Standard window presets.
 */
export const WINDOW_PRESETS: WindowPreset[] = [
  {
    id: 'standard',
    name: 'Standard Window',
    description: 'Standard residential window',
    width: 1.2,
    height: 1.5,
    sillHeight: 0.9,
    construction: '',
  },
  {
    id: 'large',
    name: 'Large Window',
    description: 'Large picture window',
    width: 2.4,
    height: 1.8,
    sillHeight: 0.6,
    construction: '',
  },
  {
    id: 'small',
    name: 'Small Window',
    description: 'Small bathroom/utility window',
    width: 0.6,
    height: 0.6,
    sillHeight: 1.5,
    construction: '',
  },
  {
    id: 'floor-to-ceiling',
    name: 'Floor to Ceiling',
    description: 'Full height glazing',
    width: 1.5,
    height: 2.4,
    sillHeight: 0.1,
    construction: '',
  },
];

/**
 * Standard door presets.
 */
export const DOOR_PRESETS: DoorPreset[] = [
  {
    id: 'single',
    name: 'Single Door',
    description: 'Standard single door',
    width: 0.9,
    height: 2.1,
    construction: '',
  },
  {
    id: 'double',
    name: 'Double Door',
    description: 'Double door entry',
    width: 1.8,
    height: 2.1,
    construction: '',
  },
  {
    id: 'sliding',
    name: 'Sliding Glass Door',
    description: 'Sliding patio door',
    width: 1.8,
    height: 2.1,
    construction: '',
  },
];
